import React, { useState, useEffect } from 'react';
import { Button, Tag, Space, Input, Modal, Form, Select, Divider, Image, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { FlexibleTable } from '../flexibleTable/flexibleTable.component';
import { listFoods, updateFood, addFood, removeFood } from '../../services/food.service';
import { PageHeader } from '../pageHeader/pageHeader.component';
import Diet from '../../enums/diet.enum';
import './adminFoods.scss';

const { Option } = Select;

export const AdminFoods = () => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentFood, setCurrentFood] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [form] = Form.useForm();
  const [filteredCount, setFilteredCount] = useState(0);
  const [servingSizeOptions, setServingSizeOptions] = useState([]);

  useEffect(() => {
    fetchFoods();
  }, []);

  const fetchFoods = async () => {
    try {
      setLoading(true);
      const response = await listFoods();
      // Store all foods in state
      setFoods(response);
      // Initial filtering is handled separately
      handleSearchFilter(searchTerm, response);
    } catch (error) {
      console.error('Error fetching foods:', error);
      message.error('Failed to fetch foods');
    } finally {
      setLoading(false);
    }
  };
  
  const handleServingSizeChange = (value) => {
    const selectedOption = servingSizeOptions.find(option => option._id === value);

    console.log(selectedOption);
    if (selectedOption) {
      // Update the form with the selected serving size details
      form.setFieldsValue({
        typicalServingSizeGrams: selectedOption.servingSizeGrams,
        typicalServingSizeDescription: selectedOption.servingSizeDescription
      });
    }
  };

  // Create a separate function to handle search filtering
  const handleSearchFilter = (term, foodsList = foods) => {
    if (!term) {
      return foodsList;
    }
    
    // Case-insensitive search that trims spaces
    const normalizedTerm = term.toLowerCase().trim();
    
    // Filter foods that contain the search term in the name
    return foodsList.filter(food => 
      food.name && food.name.toLowerCase().includes(normalizedTerm)
    );
  };
  
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // // Apply the filtering but keep the original foods array intact
    // const filteredFoods = handleSearchFilter(term);
    // FlexibleTable.updateData(filteredFoods);
  };

  const handleEdit = (food) => {
    setCurrentFood(food);
    setImageUrl(food.photoUrl || '');
    form.setFieldsValue({
      ...food,
      excludedDiets: food.excludedDiets || []
    });
    setEditModalVisible(true);
    setServingSizeOptions(food.servingSizeOptions);
  };


  const handleCreate = () => {
    setCurrentFood(null);
    form.resetFields();
    setImageUrl('');
    setEditModalVisible(true);
  };

  const handleDelete = async (foodId) => {
    try {
      await removeFood(foodId);
      message.success('Food deleted successfully');
      fetchFoods();
    } catch (error) {
      console.error('Error deleting food:', error);
      message.error('Failed to delete food');
    }
  };

  const handleModalClose = () => {
    // Clear form fields
    form.resetFields();
    
    // Clear serving size options
    setServingSizeOptions([]);
    
    // Reset current food
    setCurrentFood(null);
    
    // Reset image URL
    setImageUrl('');
    
    // Close the modal
    setEditModalVisible(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (currentFood) {
        await updateFood(currentFood._id, values);
        message.success('Food updated successfully');
      } else {
        await addFood(values);
        message.success('Food created successfully');
      }
      handleModalClose();
      fetchFoods();
    } catch (error) {
      console.error('Error saving food:', error);
      message.error('Failed to save food');
    }
  };

  const highlightText = (text, highlight) => {
    if (!highlight || !text) return text;
    
    const normalizedText = text;
    const normalizedHighlight = highlight.toLowerCase().trim();
    
    if (!normalizedHighlight) return text;
    
    const parts = normalizedText.split(new RegExp(`(${normalizedHighlight})`, 'gi'));
    
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === normalizedHighlight ? (
            <span key={i} style={{ backgroundColor: '#ffc069', padding: 0 }}>
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  // Define table columns
  const columns = [
    {
      title: 'Image',
      dataIndex: 'photoUrl',
      key: 'photoUrl',
      width: 80,
      render: (photoUrl) => (
        photoUrl ? (
          <Image 
            src={photoUrl} 
            alt="Food" 
            width={60} 
            height={60} 
            style={{ objectFit: 'cover' }}
            fallback="data:image/png;base64,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"
          />
        ) : (
          <div className="no-image-placeholder">No Image</div>
        )
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      className: 'name-column',
      fixed: 'left',
      render: (name) => highlightText(name, searchTerm)

    },
    {
      title: 'Serving Size',
      key: 'servingSize',
      width: 150,
      render: (_, record) => (
        <span>{record.typicalServingSizeDescription || '-'} ({record.typicalServingSizeGrams}g)</span>
      )
    },

    {
      title: 'Saturated Fat (g)',
      key: 'saturatedFat',
      dataIndex: 'fatSaturated',
      sorter: (a, b) => {
        const aVal = a.fatSaturated || 0;
        const bVal = b.fatSaturated || 0;
        return aVal - bVal;
      },
      render: (fatSaturated) => (
        <span>{fatSaturated || '-'}</span>
      )
    },
    {
      title: 'Unsaturated Fat (g)', 
      key: 'unsaturatedFat',
      dataIndex: 'fatUnsaturated',
      sorter: (a, b) => {
        const aVal = a.fatUnsaturated || 0;
        const bVal = b.fatUnsaturated || 0;
        return aVal - bVal;
      },
      render: (fatUnsaturated) => (
        <span>{fatUnsaturated || '-'}</span>
      )
    },
    {
        title: "Omega-3 (g)",
        key: "omega3Total",
        dataIndex: "omega3Total",
        sorter: (a, b) => {
            const aVal = a.omega3Total || 0;
            const bVal = b.omega3Total || 0;
            return aVal - bVal;
        },
        render: (omega3Total) => (
            <span>{omega3Total || '-'}</span>
        )
    },
    {
        title: "Omega-6 (g)",
        key: "omega6Total",
        dataIndex: "omega6Total",
        sorter: (a, b) => {
            const aVal = a.omega6Total || 0;
            const bVal = b.omega6Total || 0;
            return aVal - bVal;
        },
        render: (omega6Total) => (
            <span>{omega6Total || '-'}</span>
        )
    },
    {   
      title: 'EPA (g)',
      key: 'epa',
      dataIndex: 'omega3EPA',
      sorter: (a, b) => {
        const aVal = a.omega3EPA || 0;
        const bVal = b.omega3EPA || 0;
        return aVal - bVal;
      },
      render: (omega3EPA) => (
        <span>{omega3EPA || '-'}</span>
      )
    },
    {
      title: 'DHA (g)',
      key: 'dha',
      dataIndex: 'omega3DHA',
      sorter: (a, b) => {
        const aVal = a.omega3DHA || 0;
        const bVal = b.omega3DHA || 0;
        return aVal - bVal;
      },
      render: (omega3DHA) => (
        <span>{omega3DHA || '-'}</span>
      )
    },
    {
      title: 'DPA (g)',
      key: 'dpa',
      dataIndex: 'omega3DPA',
      sorter: (a, b) => {
        const aVal = a.omega3DPA || 0;
        const bVal = b.omega3DPA || 0;
        return aVal - bVal;
      },
      render: (omega3DPA) => (
        <span>{omega3DPA || '-'}</span>
      )
    },
    {
        title: "Protein (g)",
        key: "protein",
        dataIndex: "protein",
        sorter: (a, b) => {
            const aVal = a.protein || 0;
            const bVal = b.protein || 0;
            return aVal - bVal;
        },
        render: (protein) => (
            <span>{protein || '-'}</span>
        )
    },
    {
        title: "Carbs (g)",
        key: "carbohydrate",
        dataIndex: "carbohydrate",
        sorter: (a, b) => {
            const aVal = a.carbohydrate || 0;
            const bVal = b.carbohydrate || 0;
            return aVal - bVal;
        },
        render: (carbohydrate) => (
            <span>{carbohydrate || '-'}</span>
        )
    },
 
    {
      title: 'Diet Restrictions',
      dataIndex: 'excludedDiets',
      key: 'excludedDiets',
      render: (diets) => (
        <Space wrap>
          {diets && diets.length > 0 ? diets.map(diet => (
            <Tag color="blue" key={diet}>{diet}</Tag>
          )) : <span>None</span>}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)} 
          />
          {/* <Button 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record._id)} 
            danger 
            size="small"
          /> */}
        </Space>
      )
    }
  ];


  return (
    <div className="admin-foods">
    <PageHeader 
      title="Foods Administration" 
      actions={[
        <Input 
          key="search"
          placeholder="Search foods by name" 
          prefix={<SearchOutlined />}
          onChange={handleSearch}
          value={searchTerm}
          style={{ width: 250 }}
          allowClear
          autoFocus // Automatically focus the search field
        />,
        <Button 
          key="add"
          icon={<PlusOutlined />} 
          onClick={handleCreate}
          style={{ marginLeft: 10 }}
          size="large"
        >
        </Button>
      ]}
    />
      
    <FlexibleTable 
        records={handleSearchFilter(searchTerm, foods)} // Always use filtered results
        columns={columns} 
        loading={loading}
        rowKey="_id"
        setFilteredCount={setFilteredCount}
        emptyText={searchTerm ? `No foods matching "${searchTerm}"` : "No foods found"}
        scroll={{ x: 'max-content' }} // Make the table horizontally scrollable for many columns
        //sticky
      />
      
      {/* Edit Modal */}
      <Modal
        title={currentFood ? 'Edit Food' : 'Add New Food'}
        visible={editModalVisible}
        onCancel={handleModalClose}
        onOk={handleSubmit}
        width={700}
        destroyOnClose={true}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            excludedDiets: [],
            typicalServingSizeDescription: '100g',
            typicalServingSizeGrams: 100
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter food name' }]}
          >
            <Input placeholder="Enter food name" />
          </Form.Item>


          <Form.Item
            name="photoUrl"
            label="Photo URL"
          >
            <Input placeholder="Enter photo URL" />
          </Form.Item>
          
          
          <Form.Item
            name="excludedDiets"
            label="Diet Restrictions"
            tooltip="Select diets for which this food is excluded"
          >
            <Select
              mode="multiple"
              placeholder="Select diet restrictions"
              style={{ width: '100%' }}
            >
              {Object.values(Diet).map(diet => (
                <Option key={diet} value={diet}>{diet}</Option>
              ))}
            </Select>
          </Form.Item>

              <Form.Item
                // name="selectedServingSize"
                label="Seleect Typical Serving Size"
                rules={[{ required: true, message: 'Please select a serving size' }]}
            >
                <Select 
                placeholder="Select serving size"
                onChange={handleServingSizeChange}
                >
                {servingSizeOptions.map(option => (
                    <Option key={option._id} value={option._id}>
                    {option.servingSizeDescription} ({option.servingSizeGrams}g)
                    </Option>
                ))}
                </Select>
            </Form.Item>

          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Item
              name="typicalServingSizeDescription"
              label="Typical Serving Description"
              style={{ flex: 1 }}
            >
                <Input type="text" />
            </Form.Item>
            
            <Form.Item
              name="typicalServingSizeGrams"
              label="Typical Serving Grams"
              style={{ flex: 1 }}
            >
              <Input type="number" min={0} step={0.1} />
            </Form.Item>
          </div>
                    

          <Divider orientation="left">Fats</Divider>
  
  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
 
    <Form.Item
      name="fatSaturated"
      label="Saturated Fat (g)"
      style={{ width: 'calc(33% - 7px)' }}
    >
      <Input type="number" min={0} step={0.01} />
    </Form.Item>
    
    <Form.Item
      name="fatMonounsaturated"
      label="Monounsaturated Fat (g)"
      style={{ width: 'calc(33% - 7px)' }}
    >
      <Input type="number" min={0} step={0.01} />
    </Form.Item>
    
    <Form.Item
      name="fatPolyunsaturated"
      label="Polyunsaturated Fat (g)"
      style={{ width: 'calc(33% - 7px)' }}
    >
      <Input type="number" min={0} step={0.01} />
    </Form.Item>
    
    <Form.Item
      name="omega3EPA"
      label="EPA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Eicosapentaenoic Acid (20:5)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
    
    <Form.Item
      name="omega3DPA"
      label="DPA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Docosapentaenoic Acid (22:5)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
  </div>
  
  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
    <Form.Item
      name="omega3DHA"
      label="DHA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Docosahexaenoic Acid (22:6)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
    
    <Form.Item
      name="omega3ALA"
      label="ALA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Alpha-Linolenic Acid (18:3)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
 
  
    <Form.Item
      name="omega6LA"
      label="LA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Linoleic Acid (18:2)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
    
    <Form.Item
      name="omega6AA"
      label="AA (g)"
      style={{ width: 'calc(33% - 7px)' }}
      tooltip="Arachidonic Acid (20:4)"
    >
      <Input type="number" min={0} step={0.001} />
    </Form.Item>
  </div>


  
        </Form>
      </Modal>
    </div>
  );
};

